import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ShieldCheck } from "lucide-react";
import { getAppBySlug } from "../data/site";
import { STAGGER_AFTER_DETAIL_BLOCKS_STEP, staggerDelay, staggerMs } from "../lib/stagger";
import AppDistributionLinks from "../components/AppDistributionLinks";
import PageMeta from "../components/PageMeta";
import AppOwnershipBadge from "../components/AppOwnershipBadge";

export default function AppDetail() {
  const { slug } = useParams<{ slug: string }>();
  const app = slug ? getAppBySlug(slug) : undefined;
  const hasEnhancedDetail = Boolean(app?.hero && app.features?.length && app.detailNotes?.length);

  if (!app) {
    return (
      <div className="flex flex-col items-center gap-6 py-32 text-center">
        <PageMeta title="App not found | Sherxon" description="The requested app page could not be found." />
        <p className="text-lg text-muted-foreground">Project not found.</p>
        <Link
          to="/apps"
          className="inline-flex items-center gap-1.5 text-sm text-primary underline underline-offset-4 transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Back to all apps
        </Link>
      </div>
    );
  }

  if (hasEnhancedDetail && app.hero && app.features && app.detailNotes) {
    return (
      <div className="relative flex flex-col gap-14 py-20 md:gap-[4.5rem] md:py-28">
        <PageMeta
          title={app.meta?.title ?? `${app.name} | Sherxon`}
          description={app.meta?.description ?? app.description}
          canonicalPath={app.meta?.canonicalPath ?? `/apps/${app.slug}`}
        />

        <div
          className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[680px] -translate-x-1/2 rounded-full opacity-60 blur-[120px]"
          style={{ background: "radial-gradient(circle, oklch(0.68 0.13 145 / 32%) 0%, transparent 68%)" }}
        />
        <div
          className="pointer-events-none absolute top-52 right-[-8rem] h-60 w-60 rounded-full opacity-30 blur-[110px]"
          style={{ background: "radial-gradient(circle, oklch(0.77 0.12 85 / 28%) 0%, transparent 72%)" }}
        />

        <Link
          to="/apps"
          className="stagger-in inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-primary"
          style={{ animationDelay: staggerDelay(0) }}
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All apps
        </Link>

        <section className="grid gap-6 md:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)] md:items-start">
          <div className="flex flex-col gap-6">
            {(app.hero.eyebrow || app.ownership) ? (
              <div
                className="stagger-in flex items-start justify-between gap-3"
                style={{ animationDelay: staggerDelay(1) }}
              >
                {app.hero.eyebrow ? (
                  <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border/70 bg-card/60 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {app.hero.eyebrow}
                  </div>
                ) : <div />}
                {app.ownership ? (
                  <AppOwnershipBadge ownership={app.ownership} className="px-4 py-1.5 tracking-[0.24em]" />
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-col gap-4">
              <h1
                className="stagger-in max-w-3xl font-display text-5xl leading-[0.94] font-bold tracking-tight sm:text-6xl"
                style={{ animationDelay: staggerDelay(2) }}
              >
                {app.hero.title}
              </h1>
              <p
                className="stagger-in max-w-2xl text-lg leading-relaxed text-foreground/85 sm:text-xl"
                style={{ animationDelay: staggerDelay(3) }}
              >
                {app.hero.summary}
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {app.hero.supporting.map((paragraph, index) => (
                <p
                  key={paragraph}
                  className="stagger-in max-w-2xl text-base leading-relaxed text-muted-foreground/80"
                  style={{ animationDelay: staggerDelay(4 + index) }}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {app.hero.signals?.length ? (
              <div
                className="stagger-in flex flex-wrap gap-2.5"
                style={{ animationDelay: staggerDelay(6) }}
              >
                {app.hero.signals.map((signal) => (
                  <span
                    key={signal}
                    className="rounded-full border border-border/80 bg-secondary/35 px-4 py-1.5 text-sm text-foreground/85"
                  >
                    {signal}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div
            className="stagger-in relative overflow-hidden rounded-[1.8rem] border border-border/70 bg-card/70 p-6 shadow-[0_18px_80px_-42px_rgba(0,0,0,0.8)] backdrop-blur-xl"
            style={{ animationDelay: staggerDelay(4) }}
          >
            <div
              className="pointer-events-none absolute inset-x-5 top-0 h-28 rounded-b-[2rem] opacity-80 blur-2xl"
              style={{ background: "linear-gradient(180deg, oklch(0.7 0.12 145 / 22%), transparent)" }}
            />
            <div className="relative flex flex-col gap-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
                    Public app home
                  </span>
                  <h2 className="font-display text-3xl leading-none">{app.name}</h2>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  {app.ownership ? (
                    <AppOwnershipBadge ownership={app.ownership} />
                  ) : null}
                  <div className="rounded-full border border-border/80 bg-background/80 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    Mobile first
                  </div>
                </div>
              </div>

              <p className="text-sm leading-relaxed text-muted-foreground/80">
                {app.tagline}
              </p>

              <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-1">
                <div className="rounded-2xl border border-border/70 bg-background/45 p-4">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    Auth
                  </div>
                  <div className="mt-2 text-sm text-foreground">Google sign-in</div>
                </div>
                <div className="rounded-2xl border border-border/70 bg-background/45 p-4">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    Platforms
                  </div>
                  <div className="mt-2 text-sm text-foreground">iPhone and Android</div>
                </div>
                <div className="rounded-2xl border border-border/70 bg-background/45 p-4">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    Listing status
                  </div>
                  <div className="mt-2 text-sm text-foreground">Store links pending</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="flex flex-col gap-6">
          <div
            className="stagger-in flex items-center justify-between gap-4"
            style={{ animationDelay: staggerDelay(7) }}
          >
            <h2 className="font-display text-3xl sm:text-4xl">Core product surface</h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {app.features.map((feature, index) => (
              <article
                key={feature.title}
                className="stagger-in group relative overflow-hidden rounded-[1.5rem] border border-border/70 bg-card/55 p-6 backdrop-blur-sm transition-transform duration-300 hover:-translate-y-1"
                style={{ animationDelay: `${staggerMs(STAGGER_AFTER_DETAIL_BLOCKS_STEP + index)}ms` }}
              >
                <div
                  className="pointer-events-none absolute inset-x-4 top-0 h-20 rounded-b-[1.5rem] opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
                  style={{ background: "linear-gradient(180deg, oklch(0.71 0.11 145 / 20%), transparent)" }}
                />
                <div className="relative flex flex-col gap-3">
                  {feature.eyebrow ? (
                    <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
                      {feature.eyebrow}
                    </span>
                  ) : null}
                  <h3 className="text-xl font-semibold text-foreground">{feature.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground/80">
                    {feature.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-[minmax(0,1.1fr)_minmax(17rem,0.9fr)]">
          <div
            className="stagger-in rounded-[1.7rem] border border-border/70 bg-card/60 p-7 backdrop-blur-sm"
            style={{ animationDelay: staggerDelay(8) }}
          >
            <div className="flex flex-col gap-4">
              <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
                Why this page exists
              </span>
              <h2 className="font-display text-3xl sm:text-4xl">A stable public home for the mobile app</h2>
              <p className="text-base leading-relaxed text-muted-foreground/80">
                {app.description}
              </p>
              {app.closingNote ? (
                <div className="flex items-start gap-3 rounded-2xl border border-border/70 bg-background/45 p-4 text-sm leading-relaxed text-muted-foreground/85">
                  <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-primary/90" />
                  <p>{app.closingNote}</p>
                </div>
              ) : null}
            </div>
          </div>

          <div
            className="stagger-in rounded-[1.7rem] border border-border/70 bg-card/60 p-7 backdrop-blur-sm"
            style={{ animationDelay: staggerDelay(9) }}
          >
            <div className="flex flex-col gap-4">
              <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
                Trust details
              </span>
              <div className="space-y-4">
                {app.detailNotes.map((note) => (
                  <div key={note.label} className="border-b border-border/60 pb-4 last:border-b-0 last:pb-0">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                      {note.label}
                    </div>
                    <div className="mt-2 text-sm leading-relaxed text-foreground/88">{note.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          className="stagger-in rounded-[1.8rem] border border-border/70 bg-card/65 p-7 backdrop-blur-xl"
          style={{ animationDelay: staggerDelay(10) }}
        >
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
                Distribution
              </span>
              <h2 className="font-display text-3xl sm:text-4xl">Store presence is being prepared</h2>
              <p className="max-w-2xl text-base leading-relaxed text-muted-foreground/80">
                The public web home is live now. Mobile store badges are shown here in a pending state until official listing URLs are ready to publish.
              </p>
            </div>

            <AppDistributionLinks
              appStore={app.distribution?.appStore}
              playStore={app.distribution?.playStore}
              web={app.distribution?.web}
              appStoreUrl={app.appStoreUrl}
              playStoreUrl={app.playStoreUrl}
              webUrl={app.webUrl}
              staggerStartMs={0}
            />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-12 py-20 md:py-28">
      <PageMeta
        title={app.meta?.title ?? `${app.name} | Sherxon`}
        description={app.meta?.description ?? app.description}
        canonicalPath={app.meta?.canonicalPath ?? `/apps/${app.slug}`}
      />
      {/* Subtle page-level glow */}
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-64 w-[600px] -translate-x-1/2 rounded-full opacity-30 blur-[100px]"
        style={{ background: "oklch(0.5 0.18 260 / 40%)" }}
      />

      <Link
        to="/apps"
        className="stagger-in inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-primary"
        style={{ animationDelay: staggerDelay(0) }}
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All apps
      </Link>

      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <h1
            className="stagger-in font-display text-4xl font-bold tracking-tight sm:text-5xl"
            style={{ animationDelay: staggerDelay(1) }}
          >
            {app.name}
          </h1>
          {app.ownership ? (
            <div style={{ animationDelay: `${staggerDelay(1)}ms` }} className="stagger-in">
              <AppOwnershipBadge ownership={app.ownership} />
            </div>
          ) : null}
        </div>
        <p
          className="stagger-in text-xl text-muted-foreground"
          style={{ animationDelay: staggerDelay(2) }}
        >
          {app.tagline}
        </p>
      </div>

      <div
        className="stagger-in flex flex-wrap gap-2.5"
        style={{ animationDelay: staggerDelay(3) }}
      >
        {app.tags.map((t) => (
          <span
            key={t}
            className="rounded-full border border-border bg-secondary/30 px-4 py-1.5 text-sm text-foreground/80"
          >
            {t}
          </span>
        ))}
      </div>

      <p
        className="stagger-in max-w-2xl text-base leading-relaxed text-muted-foreground/80"
        style={{ animationDelay: staggerDelay(4) }}
      >
        {app.description}
      </p>

      <AppDistributionLinks
        appStoreUrl={app.appStoreUrl}
        playStoreUrl={app.playStoreUrl}
        webUrl={app.webUrl}
        staggerStartMs={staggerMs(STAGGER_AFTER_DETAIL_BLOCKS_STEP)}
      />
    </div>
  );
}
